"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { FaMapMarkerAlt } from "react-icons/fa";
import { locations } from "@/data/locations";
import { trackPostHogEvent } from "@/lib/posthog/client";

const locationSelectionKey = "city-selected";
const locationPreferenceCookieKey = "city-preference";
const locationPromptDismissCookieKey = "city-prompt-dismissed";
const homepageSelection = "homepage";
const locationPreferenceMaxAge = 60 * 60 * 24 * 365;
const locationPromptDismissMaxAge = 60 * 60 * 24 * 30;

type CitySlug = keyof typeof locations;

type LocationHintResponse = {
  city: string | null;
};

function getCookieValue(name: string): string | null {
  if (typeof document === "undefined") return null;

  const prefix = `${name}=`;
  const cookies = document.cookie.split(";");

  for (const cookie of cookies) {
    const trimmed = cookie.trim();
    if (!trimmed.startsWith(prefix)) continue;
    const value = trimmed.slice(prefix.length);
    return value ? decodeURIComponent(value) : null;
  }

  return null;
}

function setCookieValue(name: string, value: string, maxAgeSeconds: number) {
  if (typeof document === "undefined") return;

  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${maxAgeSeconds}; Path=/; SameSite=Lax`;
}

function clearCookie(name: string) {
  if (typeof document === "undefined") return;

  document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax`;
}

export default function GeoRedirect() {
  const router = useRouter();
  const [suggestedCity, setSuggestedCity] = useState<CitySlug | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const forcePrompt = params.get("forceLocationPrompt") === "1";
    const debugCityParam = params.get("debugCity");

    if (!forcePrompt && sessionStorage.getItem(locationSelectionKey)) return;

    const persistedPreference = getCookieValue(locationPreferenceCookieKey);
    if (!forcePrompt && persistedPreference && persistedPreference !== homepageSelection) {
      sessionStorage.setItem(locationSelectionKey, persistedPreference);
      return;
    }

    if (!forcePrompt && persistedPreference === homepageSelection) {
      // Cleanup legacy value so "stay here" behaves like a cooldown, not a permanent block.
      clearCookie(locationPreferenceCookieKey);
    }

    if (!forcePrompt && getCookieValue(locationPromptDismissCookieKey)) return;

    let active = true;

    const fetchLocationHint = async () => {
      try {
        const hintUrl = debugCityParam
          ? `/api/location-hint?debugCity=${encodeURIComponent(debugCityParam)}`
          : "/api/location-hint";

        // Note: deliberately no `cache: "no-store"` here — that header
        // disqualifies the page from the back/forward cache (bf-cache).
        // The /api/location-hint route sets its own short-TTL cache headers,
        // and we're inside an effect that runs once per session anyway, so
        // the browser's default behaviour is fine.
        const res = await fetch(hintUrl, { method: "GET" });
        if (!res.ok) return;

        const payload = (await res.json()) as LocationHintResponse;
        if (!payload.city || !active) return;
        if (!(payload.city in locations)) return;

        setSuggestedCity(payload.city as CitySlug);
        setShowPrompt(true);
        trackPostHogEvent("location_prompt_shown", {
          suggested_city: payload.city,
        });
      } catch {
        // No-op: we silently skip prompting when hinting fails.
      }
    };

    void fetchLocationHint();

    return () => {
      active = false;
    };
  }, []);

  const setSessionSelection = (selection: string) => {
    sessionStorage.setItem(locationSelectionKey, selection);
  };

  const setLocationPreference = (selection: string) => {
    setSessionSelection(selection);
    setCookieValue(locationPreferenceCookieKey, selection, locationPreferenceMaxAge);
  };

  const handleStayOnHomepage = () => {
    setSessionSelection(homepageSelection);
    setCookieValue(locationPromptDismissCookieKey, "1", locationPromptDismissMaxAge);
    setShowPrompt(false);
    trackPostHogEvent("location_prompt_stayed", {
      suggested_city: suggestedCity,
    });
  };

  const handleDismiss = () => {
    setSessionSelection(homepageSelection);
    setCookieValue(locationPromptDismissCookieKey, "1", locationPromptDismissMaxAge);
    setShowPrompt(false);
    trackPostHogEvent("location_prompt_dismissed", {
      suggested_city: suggestedCity,
    });
  };

  const handleSwitchToCity = () => {
    if (!suggestedCity) return;

    setLocationPreference(suggestedCity);
    setShowPrompt(false);
    trackPostHogEvent("location_prompt_switched", {
      suggested_city: suggestedCity,
      destination: `/${suggestedCity}`,
    });
    router.replace(`/${suggestedCity}`);
  };

  if (!showPrompt || !suggestedCity) return null;

  const suggestedCityName = locations[suggestedCity].name;

  return (
    <div className="fixed inset-x-0 bottom-4 z-[70] px-4">
      <div className="mx-auto flex max-w-2xl flex-col gap-4 rounded-2xl border border-gray-200 bg-white/95 px-5 py-4 shadow-2xl shadow-black/15 backdrop-blur sm:flex-row sm:items-center">
        <div className="flex items-start gap-3 sm:items-center">
          <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#DC3332]/10 text-[#DC3332] sm:mt-0">
            <FaMapMarkerAlt className="text-sm" />
          </div>
          <div>
            <p className="font-['Outfit'] text-[15px] font-semibold text-[#1C1C1C]">
              Looks like you&apos;re near {suggestedCityName}
            </p>
            <p className="text-xs text-gray-500">
              Want to switch to your local service page?
            </p>
          </div>
        </div>

        <div className="flex flex-1 justify-end gap-2">
          <button
            type="button"
            onClick={handleStayOnHomepage}
            data-ph-event="location_prompt_stay_clicked"
            data-ph-location="geo_prompt"
            data-ph-suggested-city={suggestedCityName}
            className="rounded-full border border-[#1C1C1C] bg-[#1C1C1C] px-4 py-2 text-xs font-semibold uppercase tracking-wide !text-white transition-colors hover:bg-[#0f0f0f]"
            style={{ color: "#FFFFFF" }}
          >
            Stay Here
          </button>
          <button
            type="button"
            onClick={handleSwitchToCity}
            data-ph-event="location_prompt_switch_clicked"
            data-ph-location="geo_prompt"
            data-ph-suggested-city={suggestedCityName}
            className="rounded-full border border-[#DC3332] bg-[#DC3332] px-4 py-2 text-xs font-semibold uppercase tracking-wide !text-white transition-colors hover:bg-[#c22d2c]"
            style={{ color: "#FFFFFF" }}
          >
            Switch
          </button>
        </div>

        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss location prompt"
          data-ph-event="location_prompt_dismiss_clicked"
          data-ph-location="geo_prompt"
          data-ph-suggested-city={suggestedCityName}
          className="absolute right-3 top-2.5 text-xl leading-none text-gray-400 transition-colors hover:text-gray-600 sm:static sm:text-base"
        >
          ×
        </button>
      </div>
    </div>
  );
}
